import {
  useBusinessEntitiesOfCompany,
  usePersonsInCompany,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  NumberInput,
  RadioGroup,
  RadioGroupItem,
  SingleCombobox,
} from '@commenda/kakashi';
import { VotingRightsType } from '@commenda/types/src/cap-table/types';
import { fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { StakeholderRelationshipType, StakeholderType } from '@prisma/client';
import { X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNotification } from '../../../../hooks/useNotification';
import { MemberFormFields, memberFormSchema } from './schema';

const AddMemberModal = ({
  isOpen,
  onClose,
  businessEntityId,
  companyId,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  businessEntityId: number;
  companyId: number;
  onSuccess: () => void;
}) => {
  const { data: businessEntities, error: businessEntitiesError } =
    useBusinessEntitiesOfCompany(companyId);

  const { data: persons, error: personsError } =
    usePersonsInCompany(businessEntityId);

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm<MemberFormFields>({
    resolver: zodResolver(memberFormSchema),
    defaultValues: {
      memberType: 'director',
      directorId: null,
      corporationId: null,
      isAddingNewMember: false,
      newMember: null,
      capitalContribution: {
        amount: 0,
        currency: 'USD',
      },
      membershipInterestPercentage: 0,
      profitContributionPercentage: 0,
      lossContributionPercentage: 0,
      numberOfVotingRights: 0,
    },
  });

  const closeModal = () => {
    reset();
    onClose();
  };

  const { setNotification } = useNotification();

  const memberType = watch('memberType');
  const isAddingNewMember = watch('isAddingNewMember');

  if (!businessEntities || !persons) {
    if (businessEntitiesError) console.error(businessEntitiesError);
    if (personsError) console.error(personsError);

    return (
      <Dialog open={isOpen} onOpenChange={closeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Member</DialogTitle>
          </DialogHeader>
          <div className="p-3xl flex flex-col gap-xl min-h-[200px] justify-center">
            <LoadingIndicator size={48} _className="mx-auto" />
          </div>
          <DialogFooter>
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const directorOptions =
    persons?.persons?.map((person) => ({
      label: [person.firstName, person.lastName].filter(Boolean).join(' '),
      value: person.id.toString(),
    })) || [];

  const corporationOptions = businessEntities?.entities
    ?.map((corporation) => {
      if (
        corporation.id !== businessEntityId &&
        corporation.generalCorporationInfo?.legalName &&
        corporation.generalCorporationInfo.legalName.trim()
      ) {
        return {
          label: corporation.generalCorporationInfo.legalName,
          value: corporation.id.toString(),
        };
      }
      return null;
    })
    .filter(Boolean);

  const onSubmit = async (data: MemberFormFields) => {
    // Handle form submission
    console.log(data);

    const stakeholderType =
      watch('memberType') === 'director'
        ? StakeholderType.INDIVIDUAL
        : StakeholderType.INSTITUTION;

    const defaultRequestBody = {
      corporationIdToBuySharesIn: businessEntityId,
      type: stakeholderType,
      relationshipType: StakeholderRelationshipType.OTHER,
      nationality: 'GB',
      companyId,
      holdingSpecificFields: {
        capitalContribution: {
          amount: data.capitalContribution.amount,
          currency: data.capitalContribution.currency,
        },
        membershipInterest: {
          percentage: data.membershipInterestPercentage,
        },
        profitContributionPercentage: data.profitContributionPercentage,
        lossContributionPercentage: data.lossContributionPercentage,
        votingRights: {
          type: VotingRightsType.PROPORTIONAL,
          value: data.numberOfVotingRights,
          descriptionOfRights: '',
        },
      },
    };

    const requestBody = {
      ...(stakeholderType === StakeholderType.INDIVIDUAL
        ? isAddingNewMember
          ? {
              additionalInformation: {
                primaryContact: {
                  name: data.newMember.name,
                  emails: [data.newMember.email],
                },
                type: StakeholderType.INDIVIDUAL,
              },
              ...defaultRequestBody,
            }
          : { directorId: data.directorId, ...defaultRequestBody }
        : {
            corporationId: data.corporationId,
            ...defaultRequestBody,
          }),
    };

    const response = await fetcher('capTable/stakeholding', {
      method: 'POST',
      body: requestBody,
    });

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please try again later, or contact team Commenda.',
      });
      return false;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Successfully added the stakeholder',
    });

    await onSuccess();

    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="overflow-hidden">
        <DialogHeader>
          <DialogTitle>Add Member</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-3xl flex flex-col gap-3xl max-h-[50vh] overflow-y-auto">
            <FieldWrapper>
              <Label>Type of Member</Label>
              <RadioGroup
                className="flex items-center gap-3xl"
                value={memberType}
                onValueChange={(value: 'director' | 'corporation') => {
                  setValue('memberType', value);
                  setValue('directorId', null);
                  setValue('corporationId', null);
                  setValue('newMember', null);
                  setValue('isAddingNewMember', false);
                }}
              >
                <div className="flex items-center gap-lg">
                  <RadioGroupItem value="director" id="director" />
                  <Label htmlFor="director">Person</Label>
                </div>
                <div className="flex items-center gap-lg">
                  <RadioGroupItem value="corporation" id="corporation" />
                  <Label htmlFor="corporation">Entity</Label>
                </div>
              </RadioGroup>
            </FieldWrapper>

            {memberType === 'director' && !isAddingNewMember && (
              <FieldWrapper>
                <Label>Select Person</Label>
                <SingleCombobox
                  error={errors?.directorId?.message}
                  options={directorOptions}
                  value={watch('directorId')}
                  setValue={(dirId) => {
                    setValue('directorId', dirId);
                  }}
                  addItemConfig={{
                    shouldAllowAdd: true,
                    placeholder: 'Add new member',
                    onAddItem: () => {
                      clearErrors('directorId');
                      setValue('isAddingNewMember', true);
                      setValue('newMember', { name: '', email: '' });
                    },
                  }}
                />
                {errors?.directorId && (
                  <InputErrorMessage message={errors.directorId.message} />
                )}
              </FieldWrapper>
            )}

            {memberType === 'corporation' && !isAddingNewMember && (
              <FieldWrapper>
                <Label>Select Entity</Label>
                <SingleCombobox
                  error={errors?.corporationId?.message}
                  options={corporationOptions}
                  value={watch('corporationId')}
                  setValue={(corpId) => {
                    setValue('corporationId', corpId);
                  }}
                  addItemConfig={{
                    shouldAllowAdd: true,
                    placeholder: 'Add new member',
                    onAddItem: () => {
                      setValue('isAddingNewMember', true);
                      setValue('newMember', { name: '', email: '' });
                    },
                  }}
                />
                {errors?.corporationId && (
                  <InputErrorMessage message={errors.corporationId.message} />
                )}
              </FieldWrapper>
            )}

            {isAddingNewMember && (
              <FieldWrapper>
                <div className="flex items-center justify-between">
                  <Label>Add New Member</Label>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => {
                      setValue('isAddingNewMember', false);
                      setValue('newMember', null);
                      setValue('directorId', null);
                      setValue('corporationId', null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-col gap-md">
                  <Input
                    placeholder="Name"
                    value={watch('newMember.name')}
                    onChange={(e) => {
                      clearErrors('newMember.name');
                      setValue('newMember.name', e.target.value);
                    }}
                    error={errors?.newMember?.name?.message}
                  />
                  <Input
                    placeholder="Email address"
                    value={watch('newMember.email')}
                    onChange={(e) => {
                      clearErrors('newMember.email');
                      setValue('newMember.email', e.target.value);
                    }}
                    error={errors?.newMember?.email?.message}
                  />
                </div>
              </FieldWrapper>
            )}

            <FieldWrapper>
              <Label>Capital Contribution</Label>
              <NumberInput
                value={watch('capitalContribution.amount')}
                placeholder="Enter capital contribution"
                onChange={(number) => {
                  clearErrors('capitalContribution');
                  setValue(
                    'capitalContribution',
                    {
                      amount: number,
                      currency: watch('capitalContribution.currency'),
                    },
                    {
                      shouldValidate: true,
                    }
                  );
                }}
                error={
                  errors?.capitalContribution
                    ? 'Please enter a valid amount'
                    : null
                }
              />
            </FieldWrapper>

            <FieldWrapper>
              <Label>Membership Interest Percentage</Label>
              <NumberInput
                min={0}
                max={100}
                placeholder="Enter membership interest percentage"
                value={watch('membershipInterestPercentage')}
                onChange={(number) => {
                  clearErrors('membershipInterestPercentage');
                  setValue('membershipInterestPercentage', number, {
                    shouldValidate: true,
                  });
                }}
                error={errors?.membershipInterestPercentage?.message}
              />
            </FieldWrapper>

            <FieldWrapper>
              <Label>Profit Contribution Percentage</Label>
              <NumberInput
                min={0}
                max={100}
                placeholder="Enter profit contribution percentage"
                value={watch('profitContributionPercentage')}
                onChange={(number) => {
                  clearErrors('profitContributionPercentage');
                  setValue('profitContributionPercentage', number, {
                    shouldValidate: true,
                  });
                }}
                error={errors?.profitContributionPercentage?.message}
              />
            </FieldWrapper>

            <FieldWrapper>
              <Label>Loss Contribution Percentage</Label>
              <NumberInput
                min={0}
                max={100}
                placeholder="Enter loss contribution percentage"
                value={watch('lossContributionPercentage')}
                onChange={(number) => {
                  clearErrors('lossContributionPercentage');
                  setValue('lossContributionPercentage', number, {
                    shouldValidate: true,
                  });
                }}
                error={errors?.lossContributionPercentage?.message}
              />
            </FieldWrapper>

            <FieldWrapper>
              <Label>Number of Voting Rights</Label>
              <NumberInput
                min={0}
                placeholder="Enter voting rights"
                value={watch('numberOfVotingRights')}
                onChange={(number) => {
                  clearErrors('numberOfVotingRights');
                  setValue('numberOfVotingRights', number, {
                    shouldValidate: true,
                  });
                }}
                error={
                  errors?.numberOfVotingRights?.message
                    ? 'Please enter a valid number'
                    : null
                }
              />
            </FieldWrapper>
          </div>
          <DialogFooter>
            <Button variant="secondary" size="sm" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="submit">
              Add Member
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMemberModal;
